import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Camera,
  Check,
  Download,
  Droplets,

  Gauge,
  History,
  Link2,
  Loader2,
  Save,
  ScanLine,
  Trash2,
  TrendingDown,
  TrendingUp,
  Wrench,
  X,
  RefreshCw,
  Phone,
} from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { AdSlot } from "@/components/AdSlot";
import { LiveCameraCapture } from "@/components/LiveCameraCapture";

import { useAuth } from "@/hooks/use-auth";
import {
  deleteDampScan,
  listMyDampScans,
  saveDampScan,
  setDampScanShared,
  type DampScanRow,
} from "@/lib/damp-scan.functions";

type Photo = {
  id: string;
  name: string;
  preview: string;
  base64: string;
  mimeType: string;
  blob: Blob;
};

export interface DampFinding {
  what: string;
  where: string;
  meaning: string;
  severity: number;
}

export interface MeterReading {
  readable: boolean;
  value: string;
  unit: string;
  mode: string;
  display: string;
  band: string;
  interpretation: string;
  nextStep: string;
  retakeAdvice: string;
  confidence: number;
}

export interface HeatSpot {
  x: number;
  y: number;
  r: number;
  intensity: number;
  label: string;
}

export interface DampResult {
  imageQuality: { ok: boolean; issues: string[]; retakeAdvice: string };
  areaGuess: string;
  riskLevel: number;
  riskLabel: string;
  estimatedMoisturePct: string;
  findings: DampFinding[];
  woodCondition: string;
  likelySource: string;
  isCondensation: boolean;
  isOldDamage: boolean;
  measurePoints: string[];
  actions: string[];
  costEstimate: string;
  urgency: string;
  confidence: number;
  summary: string;
  heatmap: HeatSpot[];
  disclaimer: string;
}

const MAX_PHOTOS = 4;
const MAX_EDGE = 1600;

const SHOT_LIST = [
  "Närbild på fläcken eller skarven, 20–30 cm ifrån",
  "Samma ställe snett från sidan med lampa — svullnad syns i motljus",
  "Översiktsbild så vi ser var på fordonet det sitter",
];

async function compress(file: File): Promise<Blob> {
  if (!file.type.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(new Error("read"));
    reader.readAsDataURL(blob);
  });
}

const RISK_TONE = [
  "border-border",
  "border-border",
  "border-accent/40",
  "border-accent",
  "border-destructive",
];

const XRAY_STYLES = `
@keyframes xray-pulse { 0%, 100% { opacity: 0.75; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
@keyframes xray-sweep { 0% { top: -12%; } 100% { top: 112%; } }
`;

function spotColor(intensity: number) {
  if (intensity >= 0.66) return "239, 68, 68"; // röd – starkt tecken
  if (intensity >= 0.33) return "245, 158, 11"; // bärnsten – misstanke
  return "59, 130, 246"; // blå – svagt
}

/** Röntgenläge: värmekarta över kundens egen foto där AI:n ser fukttecken. */
function XRayView({ photo, spots }: { photo: string; spots: HeatSpot[] }) {
  const [active, setActive] = useState(true);
  return (
    <div className="overflow-hidden rounded-2xl border border-border">
      <div className="flex items-center justify-between gap-3 border-b border-border bg-foreground/[0.03] px-4 py-2.5">
        <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <ScanLine className="h-3.5 w-3.5" aria-hidden="true" />
          Röntgenläge – här sitter tecknen
        </p>
        <button
          type="button"
          onClick={() => setActive((v) => !v)}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            active
              ? "border-accent bg-accent text-accent-foreground"
              : "border-border text-muted-foreground hover:bg-warm"
          }`}
          aria-pressed={active}
        >
          {active ? "På" : "Av"}
        </button>
      </div>
      <div className="relative">
        <style>{XRAY_STYLES}</style>
        <img src={photo} alt="Ditt foto med markerade fuktzoner" className="block w-full" />
        {active && (
          <div className="absolute inset-0" aria-hidden="true">
            {/* svepande scanlinje */}
            <div
              className="pointer-events-none absolute left-0 h-10 w-full"
              style={{
                background:
                  "linear-gradient(to bottom, transparent, rgba(125,211,252,0.25), rgba(125,211,252,0.6), rgba(125,211,252,0.25), transparent)",
                animation: "xray-sweep 3.2s linear infinite",
              }}
            />
            {spots.map((s, i) => {
              const color = spotColor(s.intensity);
              const size = s.r * 2 * 100;
              return (
                <div key={`${s.x}-${s.y}-${i}`}>
                  <div
                    className="pointer-events-none absolute rounded-full"
                    style={{
                      left: `${s.x * 100}%`,
                      top: `${s.y * 100}%`,
                      width: `${size}%`,
                      aspectRatio: "1",
                      transform: "translate(-50%, -50%)",
                      background: `radial-gradient(circle, rgba(${color},0.75) 0%, rgba(${color},0.35) 45%, transparent 72%)`,
                      mixBlendMode: "screen",
                      animation: `xray-pulse ${1.6 + (1 - s.intensity)}s ease-in-out infinite`,
                    }}
                  />
                  <span
                    className="pointer-events-none absolute rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                    style={{
                      left: `min(${s.x * 100}%, 78%)`,
                      top: `calc(${s.y * 100}% + ${(s.r * 100) / 2}% + 4px)`,
                      backgroundColor: `rgba(${color},0.92)`,
                      textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                    }}
                  >
                    {s.label || "Fukttecken"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <p className="px-4 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
        Rött = starkt tecken, gult = misstanke, blått = svagt. Markeringarna visar var på bilden
        bedömningen bygger — de är inte mätningar, en fuktmätare avgör.
      </p>
    </div>
  );
}

function RiskMeter({ level, label }: { level: number; label: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Riskbedömning
        </span>
        <span className="font-display text-lg">{label}</span>
      </div>
      <div className="mt-2 flex gap-1" aria-hidden="true">
        {[1, 2, 3, 4].map((n) => (
          <span
            key={n}
            className={`h-2 flex-1 rounded-full ${
              n <= level ? (level >= 4 ? "bg-destructive" : "bg-accent") : "bg-border"
            }`}
          />
        ))}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">Steg {level} av 4</p>
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Samma område mätt tidigare — visar om det gått åt rätt eller fel håll. */
function TrendNote({ scans, area }: { scans: DampScanRow[]; area: string }) {
  const key = area.trim().toLowerCase();
  const same = scans.filter((s) => (s.area ?? "").trim().toLowerCase() === key && key.length > 2);
  if (same.length < 2) return null;
  const newest = same[0];
  const previous = same[1];
  const diff = newest.risk_level - previous.risk_level;
  const worse = diff > 0;
  return (
    <p className="mt-3 flex items-start gap-2 rounded-2xl border border-border bg-warm p-4 text-sm leading-relaxed">
      {worse ? (
        <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
      ) : (
        <TrendingDown className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
      )}
      <span>
        Samma ställe låg på steg {previous.risk_level} den {fmtDate(previous.created_at)} och ligger
        nu på steg {newest.risk_level}.{" "}
        {diff === 0
          ? "Oförändrat sedan sist — men fortsätt följa det."
          : worse
            ? "Det har blivit sämre. Boka mätning i verkstad."
            : "Det har blivit bättre sedan sist."}
      </span>
    </p>
  );
}

export function DampAiScanner() {
  const { signedIn, user } = useAuth();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [area, setArea] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [meterValue, setMeterValue] = useState("");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<DampResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [retryWait, setRetryWait] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [history, setHistory] = useState<DampScanRow[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [camera, setCamera] = useState<"damp" | "meter" | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [meterBusy, setMeterBusy] = useState(false);
  const [meterError, setMeterError] = useState<string | null>(null);
  const [meterReading, setMeterReading] = useState<MeterReading | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const save = useServerFn(saveDampScan);
  const listMine = useServerFn(listMyDampScans);
  const setShared = useServerFn(setDampScanShared);
  const remove = useServerFn(deleteDampScan);

  useEffect(() => {
    if (!signedIn) {
      setHistory([]);
      return;
    }
    let alive = true;
    listMine({})
      .then((r) => {
        if (alive) setHistory(r.scans);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [signedIn, listMine]);

  async function handleFiles(files: FileList | File[] | null): Promise<Photo[]> {
    if (!files || files.length === 0) return [];
    setError(null);
    setBusy(true);
    const next = [...photos];
    try {
      for (const file of Array.from(files)) {
        if (next.length >= MAX_PHOTOS) break;
        if (file.size > 12 * 1024 * 1024) {
          setError(`${file.name} är för stor (max 12 MB).`);
          continue;
        }
        const blob = await compress(file);
        next.push({
          id: crypto.randomUUID(),
          name: file.name,
          preview: URL.createObjectURL(blob),
          base64: await toBase64(blob),
          mimeType: blob.type || "image/jpeg",
          blob,
        });
      }
      setPhotos(next);
      setResult(null);
      setSavedId(null);
      return next;
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  /**
   * Läser av en riktig fuktmätares display. Ger ett faktiskt mätvärde i
   * stället för en gissning, och fyller i fältet så bildanalysen får siffran.
   */
  async function readMeter(file: File) {
    setMeterBusy(true);
    setMeterError(null);
    setMeterReading(null);
    try {
      const blob = await compress(file);
      const base64 = await toBase64(blob);
      let lastText = "Vi kunde inte läsa av displayen. Försök igen.";
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await fetch("/api/read-meter", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              image: { base64, mimeType: blob.type || "image/jpeg" },
            }),
          });
          if (res.ok) {
            const reading = (await res.json()) as MeterReading;
            setMeterReading(reading);
            if (reading.readable && reading.display) setMeterValue(reading.display);
            return;
          }
          const text = (await res.text().catch(() => "")).trim();
          if (text) lastText = text;
          const terminal =
            res.headers.get("x-meter-quota") === "spent" ||
            [400, 401, 402, 403, 413].includes(res.status);
          if (terminal) break;
        } catch {
          lastText = "Vi fick ingen kontakt. Kolla uppkopplingen och försök igen.";
        }
        if (attempt < 3) await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
      setMeterError(lastText);
    } finally {
      setMeterBusy(false);
    }
  }

  /**
   * Seriös retry: servern gör redan tre försök med modellbyte. Utöver det
   * försöker klienten om automatiskt upp till tre gånger med växande paus
   * vid nätfel och tillfälliga serverfel (429 utan kvotstopp, 5xx, 502/504).
   * Kvotstopp och felaktiga bilder räknas som slutgiltiga och retryas aldrig.
   */
  async function analyze(manual = true, shots: Photo[] = photos) {
    if (shots.length === 0) {
      setError("Ladda upp minst en bild på området du misstänker.");
      return;
    }
    if (manual) setRetryable(false);
    setError(null);
    setAnalyzing(true);
    setSavedId(null);

    const MAX_TRIES = 3;
    let lastMessage = "Analysen misslyckades. Försök igen.";
    let canRetry = true;

    try {
      for (let attempt = 1; attempt <= MAX_TRIES; attempt++) {
        setAttempt(attempt);
        try {
          const res = await fetch("/api/analyze-damp", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              images: shots.map((p) => ({ base64: p.base64, mimeType: p.mimeType })),
              context: { vehicle, area, meterValue, note },
            }),
          });

          if (res.ok) {
            setResult((await res.json()) as DampResult);
            setError(null);
            setRetryable(false);
            return;
          }

          const text = (await res.text().catch(() => "")).trim();
          if (text) lastMessage = text;

          // Slutgiltiga fel: kvoten är slut, bilderna duger inte, eller
          // tjänsten är avstängd. Att skicka om ger exakt samma svar.
          const quotaSpent = res.headers.get("x-damp-quota") === "spent";
          const terminal =
            quotaSpent || [400, 401, 402, 403, 413].includes(res.status);
          if (terminal) {
            canRetry = false;
            break;
          }

          if (attempt < MAX_TRIES) {
            const retryAfter = Number(res.headers.get("retry-after") ?? "");
            const waitMs =
              Number.isFinite(retryAfter) && retryAfter > 0
                ? Math.min(retryAfter, 8) * 1000
                : 1200 * attempt;
            setRetryWait(Math.round(waitMs / 1000));
            await new Promise((r) => setTimeout(r, waitMs));
            setRetryWait(0);
            continue;
          }
        } catch {
          lastMessage =
            "Vi fick ingen kontakt med analysen — kolla uppkopplingen. Dina bilder ligger kvar.";
          if (attempt < MAX_TRIES) {
            setRetryWait(2 * attempt);
            await new Promise((r) => setTimeout(r, 1500 * attempt));
            setRetryWait(0);
            continue;
          }
        }
      }

      setError(lastMessage);
      setRetryable(canRetry);
    } finally {
      setAnalyzing(false);
      setAttempt(0);
      setRetryWait(0);
    }
  }


  async function saveScan() {
    if (!result || !user) return;
    setSaving(true);
    setError(null);
    try {
      const paths: string[] = [];
      for (const p of photos) {
        const ext = p.mimeType.includes("png") ? "png" : "jpg";
        const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("fukt-foton")
          .upload(path, p.blob, { contentType: p.mimeType, upsert: false });
        if (!upErr) paths.push(path);
      }
      const row = await save({
        data: {
          vehicle,
          area,
          note,
          meterValue,
          riskLevel: result.riskLevel,
          riskLabel: result.riskLabel,
          estimatedMoisturePct: result.estimatedMoisturePct,
          assessment: result,
          photoPaths: paths,
        },
      });
      setSavedId(row.id);
      const fresh = await listMine({});
      setHistory(fresh.scans);
    } catch {
      setError("Kunde inte spara skanningen. Försök igen.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleShare(scan: DampScanRow) {
    try {
      const row = await setShared({ data: { id: scan.id, shared: !scan.is_shared } });
      setHistory((h) => h.map((s) => (s.id === row.id ? { ...s, is_shared: row.is_shared } : s)));
      if (row.is_shared) await copyLink(row.share_token);
    } catch {
      setError("Kunde inte ändra delningen.");
    }
  }

  async function copyLink(token: string) {
    const url = `${window.location.origin}/fuktrapport/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      setError(`Kopiera länken manuellt: ${url}`);
    }
  }

  async function removeScan(id: string) {
    try {
      await remove({ data: { id } });
      setHistory((h) => h.filter((s) => s.id !== id));
      if (savedId === id) setSavedId(null);
    } catch {
      setError("Kunde inte ta bort skanningen.");
    }
  }


  return (
    <div className={`rounded-3xl border bg-card p-5 sm:p-7 ${result ? RISK_TONE[result.riskLevel] : "border-border"}`}>
      <p className="eyebrow">Verktyg · Bildanalys</p>
      <h2 className="mt-1 flex items-center gap-2 font-display text-xl md:text-2xl">
        <Droplets className="h-5 w-5 text-accent" aria-hidden="true" />
        Fuktskannern — fota stället, få besked
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Fota stället du misstänker — du får riskbedömning, mätpunkter och vad det kostar att
        åtgärda, på under en minut. Inga fält att fylla i.
      </p>

      <ol className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-semibold text-muted-foreground" aria-label="Så går det till">
        {["Fota", "Analysera", "Få besked"].map((s, i) => (
          <li key={s} className="flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/15 text-[11px] font-bold text-accent">
              {i + 1}
            </span>
            {s}
            {i < 2 && <span className="ml-1.5 text-border" aria-hidden="true">→</span>}
          </li>
        ))}
      </ol>

      {photos.length > 0 && (
        <ul className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map((p) => (
            <li
              key={p.id}
              className="group relative overflow-hidden rounded-xl border border-border bg-background"
            >
              <img src={p.preview} alt={p.name} className="aspect-square w-full object-cover" />
              <button
                type="button"
                aria-label={`Ta bort ${p.name}`}
                onClick={() => {
                  setPhotos(photos.filter((x) => x.id !== p.id));
                  setResult(null);
                }}
                className="absolute right-1 top-1 rounded-full bg-foreground/70 p-1.5 text-background transition sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <button
          type="button"
          onClick={() => setCamera("damp")}
          disabled={busy || photos.length >= MAX_PHOTOS}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
        >
          <ScanLine className="h-4 w-4" aria-hidden="true" /> Fota stället — besked direkt
        </button>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy || photos.length >= MAX_PHOTOS}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-border px-6 text-sm font-semibold transition hover:border-accent/50 disabled:opacity-50"
        >
          <Camera className="h-4 w-4 text-accent" aria-hidden="true" /> Välj bilder ur albumet
        </button>
        <button
          type="button"
          onClick={() => setCamera("meter")}
          disabled={meterBusy}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-border px-6 text-sm font-semibold transition hover:border-accent/50 disabled:opacity-50"
        >
          <Gauge className="h-4 w-4 text-accent" aria-hidden="true" /> Läs av min fuktmätare
        </button>
      </div>

      <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
        Kameran guidar dig med ram, ljus och skärpa i realtid. En telefon kan inte mäta fukthalt
        genom en vägg — bilden visar ytan, en stiftmätare ger värdet.
      </p>

      <details className="mt-3 rounded-xl border border-border/70 bg-warm/40 px-4 py-2.5 text-[12px] text-muted-foreground">
        <summary className="cursor-pointer font-semibold text-foreground">
          Så fotar du bäst — tre bilder räcker
        </summary>
        <ol className="mt-2 grid gap-1.5 pb-1 leading-snug sm:grid-cols-3">
          {SHOT_LIST.map((s, i) => (
            <li key={s} className="flex gap-2">
              <span className="text-accent">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </details>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFiles(e.currentTarget.files)}
        disabled={busy || photos.length >= MAX_PHOTOS}
        aria-label="Ladda upp bilder på det misstänkta området"
        className="hidden"
      />
      {busy && (
        <span className="mt-2 flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Förbereder bilderna…
        </span>
      )}

      {meterBusy && (
        <p className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Läser av displayen…
        </p>
      )}

      {meterError && (
        <p className="mt-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-3 text-xs font-semibold text-destructive">
          {meterError}
        </p>
      )}

      {meterReading && (
        <div className="mt-3 rounded-2xl border border-accent/40 bg-warm p-4">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Avläst från din fuktmätare
          </p>
          {meterReading.readable ? (
            <>
              <p className="mt-1 font-display text-2xl">
                {meterReading.display}{" "}
                <span className="align-middle text-sm text-muted-foreground">
                  · {meterReading.band}
                </span>
              </p>
              {meterReading.mode && (
                <p className="mt-1 text-[12px] text-muted-foreground">
                  Läge på mätaren: {meterReading.mode}
                </p>
              )}
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {meterReading.interpretation}
              </p>
              {meterReading.nextStep && (
                <p className="mt-2 text-xs font-semibold">{meterReading.nextStep}</p>
              )}
            </>
          ) : (
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {meterReading.retakeAdvice}
            </p>
          )}
        </div>
      )}

      {camera && (
        <LiveCameraCapture
          mode={camera}
          onClose={() => setCamera(null)}
          onCapture={(file) => {
            const mode = camera;
            setCamera(null);
            if (mode === "meter") {
              void readMeter(file);
              return;
            }
            // Snabbläge: fotot analyseras direkt, inga fält behöver fyllas i.
            void handleFiles([file]).then((shots) => {
              if (shots.length > 0) void analyze(true, shots);
            });
          }}
        />
      )}

      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground underline decoration-dotted underline-offset-4 transition hover:text-foreground"
      >
        {showDetails ? "Dölj detaljerna" : "Lägg till fordon, område och mätvärde (valfritt)"}
      </button>

      <div className={`${showDetails ? "mt-4" : "hidden"} grid gap-3 sm:grid-cols-3`}>

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Var på fordonet
          </span>
          <input
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="t.ex. fönsterhörn bak höger"
            className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Fordon och år
          </span>
          <input
            value={vehicle}
            onChange={(e) => setVehicle(e.target.value)}
            placeholder="t.ex. Rapido 2014"
            className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Fuktmätare visar (om du mätt)
          </span>
          <input
            value={meterValue}
            onChange={(e) => setMeterValue(e.target.value)}
            placeholder="t.ex. 21 %"
            className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
          />
        </label>
      </div>

      <label className={showDetails ? "mt-3 block" : "hidden"}>
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Beskriv kort vad du märkt
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Luktar instängt efter regn, panelen känns mjuk i nedre kanten…"
          className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
        />
      </label>

      <button
        type="button"
        onClick={() => void analyze(true)}
        disabled={analyzing || busy || photos.length === 0}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-accent px-5 py-3.5 text-sm font-semibold text-accent-foreground transition hover:opacity-90 disabled:opacity-50"
      >
        {analyzing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {retryWait > 0
              ? `Tappade kontakten – nytt försök om ${retryWait} s…`
              : attempt > 1
                ? `Försöker igen (${attempt} av 3)…`
                : "Analyserar området…"}
          </>
        ) : (
          <>
            <Camera className="h-4 w-4" aria-hidden="true" />
            {result ? "Analysera om" : "Analysera fuktrisken"}
          </>
        )}
      </button>

      {error && (
        <div className="mt-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-3.5">
          <p className="flex items-start gap-1.5 text-xs font-semibold text-destructive">
            <X className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" /> {error}
          </p>
          {retryable && (
            <>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                Vi försökte {3} gånger automatiskt. Dina bilder och uppgifter ligger kvar —
                du kan trycka en gång till utan att fota om.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void analyze(true)}
                  disabled={analyzing}
                  className="inline-flex min-h-10 items-center gap-2 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Försök igen
                </button>
                <a
                  href="mailto:info@akhusbilfritid.se?subject=Fuktbed%C3%B6mning%20%E2%80%93%20bilder%20bifogade"
                  className="inline-flex min-h-10 items-center gap-2 rounded-full border border-border px-4 text-xs text-muted-foreground transition hover:border-accent/50 hover:text-foreground"
                >
                  Mejla bilderna till oss i stället
                </a>
              </div>
            </>
          )}
        </div>
      )}


      {/* Diskret annonsyta direkt under skannerknappen. Göms medan analysen
          körs så den aldrig konkurrerar med väntesteget. */}
      {!analyzing && (
        <div className="mt-5 rounded-2xl border border-border/60 bg-warm/40 px-3 pb-3 pt-2">
          <AdSlot
            variant="in-article"
            label="Annons"
            className="my-0 [&_ins]:rounded-xl [&_ins]:overflow-hidden"
          />
        </div>
      )}


      {result && (
        <div className="mt-6 border-t border-border pt-5">
          {!result.imageQuality.ok && (
            <div className="mb-4 rounded-2xl border border-border bg-warm p-4">
              <p className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 text-accent" aria-hidden="true" /> Bilden räcker
                inte hela vägen
              </p>
              {result.imageQuality.issues.length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs leading-relaxed text-muted-foreground">
                  {result.imageQuality.issues.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              )}
              {result.imageQuality.retakeAdvice && (
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {result.imageQuality.retakeAdvice}
                </p>
              )}
            </div>
          )}

          <RiskMeter level={result.riskLevel} label={result.riskLabel} />

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[
              { k: "Område", v: result.areaGuess || "—" },
              { k: "Uppskattat fuktvärde", v: result.estimatedMoisturePct || "Kan inte bedömas på bild" },
              { k: "Brådska", v: result.urgency || "—" },
            ].map((x) => (
              <div key={x.k} className="rounded-2xl border border-border p-4">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {x.k}
                </p>
                <p className="mt-1 text-sm leading-snug">{x.v}</p>
              </div>
            ))}
          </div>

          <p className="mt-4 text-sm leading-relaxed">{result.summary}</p>

          {result.heatmap.length > 0 && photos.length > 0 && (
            <div className="mt-4">
              <XRayView photo={photos[0].preview} spots={result.heatmap} />
            </div>
          )}

          {(result.isCondensation || result.isOldDamage) && (
            <p className="mt-3 rounded-2xl border border-border bg-warm p-4 text-sm leading-relaxed text-muted-foreground">
              {result.isCondensation
                ? "Det här ser mest ut som kondens, inte en fuktskada — jämn fukt på kall yta utan missfärgat trä. Vädra och kontrollmät ändå."
                : "Spåren ser äldre ut och kan vara en redan åtgärdad skada. Mät för att avgöra om det är aktivt."}
            </p>
          )}

          {result.findings.length > 0 && (
            <div className="mt-4 rounded-2xl border border-border p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Vad vi ser
              </p>
              <ul className="mt-2 space-y-3">
                {result.findings.map((f) => (
                  <li key={f.what} className="text-sm leading-relaxed">
                    <span className="font-medium">{f.what}</span>
                    {f.where && <span className="text-muted-foreground"> · {f.where}</span>}
                    {f.meaning && (
                      <p className="mt-0.5 text-muted-foreground">{f.meaning}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.woodCondition && (
            <div className="mt-3 rounded-2xl border border-border p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Träet och plywooden
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {result.woodCondition}
              </p>
            </div>
          )}

          {result.likelySource && (
            <div className="mt-3 rounded-2xl border border-border p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Varifrån vattnet troligen kommer
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {result.likelySource}
              </p>
            </div>
          )}

          {result.measurePoints.length > 0 && (
            <div className="mt-3 rounded-2xl border border-accent/30 bg-warm p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Mät här med stiftfuktmätare
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
                {result.measurePoints.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {result.actions.length > 0 && (
            <div className="mt-3 rounded-2xl border border-border p-4">
              <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <Wrench className="h-3.5 w-3.5 text-accent" aria-hidden="true" /> Åtgärder i
                prioritetsordning
              </p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
                {result.actions.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ol>
              {result.costEstimate && (
                <p className="mt-2 text-sm">
                  <span className="font-medium">Kostnad:</span>{" "}
                  <span className="text-muted-foreground">{result.costEstimate}</span>
                </p>
              )}
            </div>
          )}

          <TrendNote scans={history} area={area} />

          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-border p-4">
            <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
              Ladda ner rapporten som PDF – bra att ta med till verkstaden eller visa en köpare.
            </p>
            <button
              type="button"
              onClick={async () => {
                setPdfBusy(true);
                try {
                  const { downloadDampReportPdf } = await import("@/lib/damp-pdf");
                  await downloadDampReportPdf({
                    result,
                    vehicle,
                    area,
                    note,
                    meterValue,
                    meterReading,
                    photos: photos.map((p) => ({ base64: p.base64, mimeType: p.mimeType })),
                  });
                } catch {
                  /* nedladdningen får aldrig krascha rapporten */
                } finally {
                  setPdfBusy(false);
                }
              }}
              disabled={pdfBusy}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-xs font-semibold disabled:opacity-50"
            >
              {pdfBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              Ladda ner som PDF
            </button>
          </div>


          <div className="mt-5 rounded-2xl border border-accent/30 bg-warm p-4">
            {signedIn ? (
              savedId ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <Check className="h-4 w-4 text-accent" aria-hidden="true" /> Sparad i din
                    fukthistorik
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      const scan = history.find((s) => s.id === savedId);
                      if (scan) void toggleShare(scan);
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold"
                  >
                    <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {history.find((s) => s.id === savedId)?.is_shared
                      ? "Kopiera delningslänk igen"
                      : "Skapa delbar länk"}
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Spara rapporten så kan du fota samma ställe nästa vår och se om det blivit
                    sämre.
                  </p>
                  <button
                    type="button"
                    onClick={saveScan}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs font-semibold text-background disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <Save className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Spara rapporten
                  </button>
                </div>
              )
            ) : (
              <p className="text-sm leading-relaxed text-muted-foreground">
                <Link to="/login" className="font-medium text-accent underline underline-offset-4">
                  Logga in
                </Link>{" "}
                för att spara rapporten, följa samma ställe över tid och skicka en delbar länk till
                köpare eller säljare.
              </p>
            )}
          </div>

          {result.riskLevel >= 2 && (
            <div className="mt-5 rounded-2xl border border-accent bg-accent/10 p-5">
              <p className="font-display text-lg leading-snug">
                Vi hittade tecken på fukt – vill du boka ett fukttest?
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                Vi mäter med stiftfuktmätare på plats, kontrollerar tätningar och skarvar och säger
                rakt ut vad som behöver göras. Ring oss så bokar vi en tid.
              </p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <a
                  href="tel:0708629743"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground transition hover:opacity-90"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" /> Ring 070-862 97 43
                </a>
                <Link
                  to="/boka-service"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-accent px-5 py-3 text-sm font-semibold transition hover:bg-accent/10"
                >
                  Boka fukttest
                </Link>
              </div>
            </div>
          )}

          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            {result.disclaimer} Säkerhet i bedömningen: {Math.round(result.confidence * 100)} %.
          </p>
        </div>
      )}

      {signedIn && history.length > 0 && (
        <div className="mt-6 border-t border-border pt-5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <History className="h-3.5 w-3.5 text-accent" aria-hidden="true" /> Din fukthistorik
          </p>
          <ul className="mt-3 space-y-2">
            {history.map((s) => (
              <li
                key={s.id}
                className={`rounded-2xl border p-4 ${RISK_TONE[Math.min(4, Math.max(0, s.risk_level))]}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {s.area || "Område ej angivet"}
                      <span className="text-muted-foreground"> · steg {s.risk_level} av 4</span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {fmtDate(s.created_at)}
                      {s.vehicle ? ` · ${s.vehicle}` : ""}
                      {s.risk_label ? ` · ${s.risk_label}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void toggleShare(s)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-[11px] font-semibold"
                    >
                      {copied === s.share_token ? (
                        <>
                          <Check className="h-3 w-3" aria-hidden="true" /> Länk kopierad
                        </>
                      ) : (
                        <>
                          <Link2 className="h-3 w-3" aria-hidden="true" />
                          {s.is_shared ? "Delad — kopiera" : "Dela"}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label="Ta bort skanningen"
                      onClick={() => void removeScan(s.id)}
                      className="rounded-full border border-border bg-card p-1.5 text-muted-foreground"
                    >
                      <Trash2 className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default DampAiScanner;
